import type {
  BuildingLayerItem,
  BuildingLayerObject,
  BuildingLayerPreset,
  BuildingSubLayer,
} from "features/dt-basic-setup/single-building/model/types";

import { type RefObject, useCallback } from "react";
import { createId } from "utils/general";

import type { CommitLayers, LayerDispatch, LayerStepState } from "../types";

import { CUSTOM_KEY } from "../constants";

export function useLayersStepActions(params: {
  activeLayer: BuildingLayerItem | undefined;
  activeSubLayer: BuildingSubLayer | undefined;
  canDraw: boolean;
  commitLayers: CommitLayers;
  dispatch: LayerDispatch;
  drawingObjectIdRef: RefObject<null | string>;
  endDrawingSession: () => void;
  layers: BuildingLayerItem[];
  layerUi: LayerStepState;
  subLayerDisplayName: (layer: BuildingLayerItem, sub: BuildingSubLayer, index: number) => string;
  updateLayer: (layerId: string, updater: (layer: BuildingLayerItem) => BuildingLayerItem) => void;
}) {
  const {
    activeLayer,
    activeSubLayer,
    canDraw,
    commitLayers,
    dispatch,
    drawingObjectIdRef,
    endDrawingSession,
    layers,
    layerUi,
    subLayerDisplayName,
    updateLayer,
  } = params;

  const updateSubLayerName = (layerId: string, subLayerId: string, name: string) => {
    updateLayer(layerId, (layer) => ({
      ...layer,
      subLayers: layer.subLayers.map((s) => (s.id === subLayerId ? { ...s, name } : s)),
    }));
  };

  const updateObjectName = (layerId: string, subLayerId: string, objectId: string, name: string) => {
    updateLayer(layerId, (layer) => ({
      ...layer,
      subLayers: layer.subLayers.map((s) =>
        s.id === subLayerId
          ? {
              ...s,
              objects: s.objects.map((o) => (o.id === objectId ? { ...o, name } : o)),
            }
          : s
      ),
    }));
  };

  const addLayerFromForm = () => {
    if (layerUi.newLayerPreset === CUSTOM_KEY && !layerUi.customLayerNameDraft.trim()) return;

    const id = createId();
    const name = layerUi.newLayerPreset === CUSTOM_KEY ? layerUi.customLayerNameDraft.trim() : "";
    const next: BuildingLayerItem = {
      id,
      name,
      preset: layerUi.newLayerPreset,
      subLayers: [],
      visible: true,
    };
    commitLayers((prev) => [...prev, next]);
    dispatch({ layerId: id, type: "ADD_LAYER_COMMITTED" });
  };

  const addSubLayer = (layerId: string, name: string, nameFromPreset = false) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const sub: BuildingSubLayer = {
      id: createId(),
      name: trimmed,
      nameFromPreset: nameFromPreset ? true : undefined,
      objects: [],
    };
    commitLayers((prev) =>
      prev.map((layer) => (layer.id === layerId ? { ...layer, subLayers: [...layer.subLayers, sub] } : layer))
    );
    if (layerId === layerUi.activeLayerId) {
      dispatch({ id: sub.id, type: "SET_ACTIVE_SUB_LAYER_ID" });
    }
  };

  const addSubLayerFromPresetKey = (layer: BuildingLayerItem, presetKey: string) => {
    addSubLayer(layer.id, presetKey, true);
  };

  const deleteSubLayer = (layerId: string, subLayerId: string) => {
    updateLayer(layerId, (layer) => ({ ...layer, subLayers: layer.subLayers.filter((s) => s.id !== subLayerId) }));
    dispatch({ subLayerId, type: "DELETE_SUB_LAYER_UI" });
  };

  const deleteLayer = useCallback(
    (layerId: string) => {
      commitLayers((prev) => prev.filter((l) => l.id !== layerId));
      dispatch({ type: "DELETE_LAYER_UI" });
    },
    [commitLayers, dispatch]
  );

  const deleteObject = (layerId: string, subLayerId: string, objectId: string) => {
    if (drawingObjectIdRef.current === objectId) {
      drawingObjectIdRef.current = null;
    }
    commitLayers((prev) =>
      prev.map((layer) =>
        layer.id === layerId
          ? {
              ...layer,
              subLayers: layer.subLayers.map((s) =>
                s.id === subLayerId ? { ...s, objects: s.objects.filter((o) => o.id !== objectId) } : s
              ),
            }
          : layer
      )
    );
    if (layerUi.selectedObjectId === objectId) dispatch({ id: null, type: "SET_SELECTED_OBJECT" });
    if (layerUi.editingObjectId === objectId) dispatch({ id: null, type: "SET_EDITING_OBJECT" });
  };

  const deleteSelectedObject = () => {
    if (!layerUi.selectedObjectId || !activeLayer || !activeSubLayer) return;
    deleteObject(activeLayer.id, activeSubLayer.id, layerUi.selectedObjectId);
  };

  const updateObjectPoint = (objectId: null | string, pointIndex: number, nextPoint: [number, number]) => {
    if (!objectId || !activeLayer || !activeSubLayer) return;
    commitLayers((prev) =>
      prev.map((layer) =>
        layer.id === activeLayer.id
          ? {
              ...layer,
              subLayers: layer.subLayers.map((s) =>
                s.id === activeSubLayer.id
                  ? {
                      ...s,
                      objects: s.objects.map((object) =>
                        object.id === objectId
                          ? {
                              ...object,
                              points: object.points.map((point, index) => (index === pointIndex ? nextPoint : point)),
                            }
                          : object
                      ),
                    }
                  : s
              ),
            }
          : layer
      )
    );
  };

  const appendPointWhileDrawing = useCallback(
    (point: [number, number]) => {
      if (!canDraw || !layerUi.isDrawing || !activeLayer || !activeSubLayer) return;

      const oid = drawingObjectIdRef.current;
      if (!oid) {
        const newId = createId();
        drawingObjectIdRef.current = newId;
        commitLayers((prev) =>
          prev.map((layer) => {
            if (layer.id !== activeLayer.id) return layer;
            return {
              ...layer,
              subLayers: layer.subLayers.map((s) => {
                if (s.id !== activeSubLayer.id) return s;
                const subIndex = Math.max(
                  0,
                  layer.subLayers.findIndex((x) => x.id === s.id)
                );
                const ordinal = s.objects.length + 1;
                const newObject: BuildingLayerObject = {
                  id: newId,
                  name: `${subLayerDisplayName(layer, s, subIndex)} ${ordinal}`,
                  points: [point],
                };
                return { ...s, objects: [...s.objects, newObject] };
              }),
            };
          })
        );
        dispatch({ id: newId, type: "SET_SELECTED_OBJECT" });
        return;
      }

      commitLayers((prev) =>
        prev.map((layer) =>
          layer.id === activeLayer.id
            ? {
                ...layer,
                subLayers: layer.subLayers.map((s) =>
                  s.id === activeSubLayer.id
                    ? {
                        ...s,
                        objects: s.objects.map((o) => (o.id === oid ? { ...o, points: [...o.points, point] } : o)),
                      }
                    : s
                ),
              }
            : layer
        )
      );
    },
    [
      activeLayer,
      activeSubLayer,
      canDraw,
      commitLayers,
      dispatch,
      drawingObjectIdRef,
      layerUi.isDrawing,
      subLayerDisplayName,
    ]
  );

  const setSubDraft = (layerId: string, value: string) => {
    dispatch({ layerId, type: "SET_SUB_LAYER_DRAFT", value });
  };

  const onToolbarDrawClick = () => {
    if (!canDraw) return;
    if (layerUi.isDrawing) {
      endDrawingSession();
    } else {
      drawingObjectIdRef.current = null;
      dispatch({ type: "START_DRAWING_SESSION" });
    }
  };

  const onToolbarEditClick = () => {
    if (canDraw) {
      dispatch({ type: "START_EDITING_SESSION" });
    }
    endDrawingSession();
  };

  const onSelectObjectRow = (layerId: string, subLayerId: string, objectId: string) => {
    drawingObjectIdRef.current = null;
    dispatch({ layerId, objectId, subLayerId, type: "SELECT_OBJECT_ROW" });
  };

  const onSelectPolygon = (objectId: string) => {
    dispatch({ objectId, type: "SELECT_POLYGON" });
    endDrawingSession();
  };

  const onSelectSubLayerRow = (layerId: string, subLayerId: string) => {
    drawingObjectIdRef.current = null;
    dispatch({ layerId, subLayerId, type: "SELECT_SUBLAYER_ROW" });
  };

  const onStartEditObject = (objectId: string) => {
    dispatch({ id: objectId, type: "SET_EDITING_OBJECT" });
    dispatch({ id: null, type: "SET_EDITING_SUBLAYER" });
  };

  const setActiveLayerId = (layerId: string) => {
    if (layerUi.activeLayerId === layerId) return;
    dispatch({ layerId, type: "SET_ACTIVE_LAYER_HEADER" });
    drawingObjectIdRef.current = null;

    const layer = layers.find((item) => item.id === layerId);
    const firstSubLayer = layer?.subLayers[0];
    if (!firstSubLayer) {
      dispatch({ id: "", type: "SET_ACTIVE_SUB_LAYER_ID" });
      dispatch({ id: null, type: "SET_SELECTED_OBJECT" });
      return;
    }

    dispatch({ id: firstSubLayer.id, type: "SET_ACTIVE_SUB_LAYER_ID" });
    dispatch({ id: firstSubLayer.objects[0]?.id ?? null, type: "SET_SELECTED_OBJECT" });
  };

  const mapActions = {
    appendPointWhileDrawing,
    deleteSelectedObject,
    endDrawingSession,
    onSelectPolygon,
    onToolbarDrawClick,
    onToolbarEditClick,
    updateObjectPoint,
  };

  const formActions = {
    addLayerFromForm,
    addSubLayer,
    addSubLayerFromPresetKey,
    deleteLayer,
    deleteObject,
    deleteSubLayer,
    onSelectObjectRow,
    onSelectSubLayerRow,
    onStartEditObject,
    setActiveLayerId,
    setCustomLayerNameDraft: (value: string) => dispatch({ type: "SET_CUSTOM_LAYER_NAME_DRAFT", value }),
    setEditingLayerId: (id: null | string) => dispatch({ id, type: "SET_EDITING_LAYER" }),
    setEditingObjectId: (id: null | string) => dispatch({ id, type: "SET_EDITING_OBJECT" }),
    setEditingSubLayerId: (id: null | string) => dispatch({ id, type: "SET_EDITING_SUBLAYER" }),
    setNewLayerPreset: (preset: BuildingLayerPreset) => dispatch({ preset, type: "SET_NEW_LAYER_PRESET" }),
    setNewSubLayerKey: (key: string) => dispatch({ key, type: "SET_NEW_SUB_LAYER_KEY" }),
    setSubDraft,
    updateLayer,
    updateObjectName,
    updateSubLayerName,
  };

  return { formActions, mapActions };
}
